import { selectCurrentTeam } from 'containers/app/appSlice';
import { useLazyGetTransactionsByPayoutIdQuery } from '../api';
import { PayoutTxnsData, TransactionsByPayoutIdPayload } from '../payouts.model';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useAppSelector } from '../../../hooks/typedHooks';
import { isPermissionDenied } from 'utils/apiUtils';
import DateUtils from 'utils/dateUtils';
import { PAYOUT_TXNS_TYPE_FILTERS } from '../constants';

export interface PayoutTransactionsQueryParams {
  status: string;
  amount: string;
  fromTimestamp: string;
  toTimestamp: string;
  search: string;
}

export const initialPayoutTxnsQueryParamsState = {
  status: '',
  amount: '',
  fromTimestamp: '',
  toTimestamp: '',
  search: ''
};

export const usePaginatedGetTransactionsByPayoutId = ({ payoutId }: { payoutId: string }) => {
  const currentTeam = useAppSelector(selectCurrentTeam);
  const [list, setList] = useState<PayoutTxnsData[]>([]);
  const [groupedList, setGroupedList] = useState({});
  const [queryParams, setQueryParams] = useState<PayoutTransactionsQueryParams>({
    ...initialPayoutTxnsQueryParamsState,
    status: `${PAYOUT_TXNS_TYPE_FILTERS.COLLECTED},${PAYOUT_TXNS_TYPE_FILTERS.REFUNDS}`
  });
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isError, setIsError] = useState<boolean>(false);
  const [hasMore, setHasMore] = useState<boolean>(true);
  const [totalListCount, setTotalListCount] = useState<number>(0);
  const [hasPermissionsToView, setHasPermissionsToView] = useState(true);
  const page = useRef(0);
  const [getPayoutTransactionsData, payoutTransactionsState] = useLazyGetTransactionsByPayoutIdQuery();

  const {
    isSuccess: isPayoutTxnsSuccess,
    data: payoutTxnsData,
    isFetching: isPayoutTxnsFetching,
    isError: isPayoutTxnsError,
    error: payoutTxnsError
  } = payoutTransactionsState ?? {};

  const { data: txnsData, metadata } = payoutTxnsData ?? {};
  const { total } = metadata ?? {};

  const isInitListCall: boolean = page.current === 0;

  const groupTxns = (data: PayoutTxnsData[]) => {
    const dateUpdatedList = data.map(item => {
      return {
        ...item,
        createdAt: DateUtils.getDateInFormat({ date: new Date(item.createdAt), dateFormat: 'EEEE, MMMM dd, yyyy' })
      };
    });
    const datesArray = dateUpdatedList.map(item => item.createdAt);
    const datesSet = new Set(datesArray);
    let dateGroupedList = {};
    datesSet.forEach(date => {
      const dateList = dateUpdatedList.filter(item => item.createdAt === date);
      dateGroupedList = { ...dateGroupedList, [date]: dateList };
    });
    return dateGroupedList;
  };

  useEffect(() => {
    if (!isPayoutTxnsFetching && isPayoutTxnsSuccess) {
      setHasPermissionsToView(true);
      const payoutTxns = txnsData ?? [];
      const updatedList = isInitListCall ? payoutTxns : [...list, ...payoutTxns];
      const payoutTxnsLeftToFetch = total ? +total - updatedList.length : 0;
      if (payoutTxnsLeftToFetch > 0 && !txnsData && txnsData?.length === 0) {
        setHasMore(false);
      } else {
        setHasMore(payoutTxnsLeftToFetch > 0);
      }
      setGroupedList(groupTxns(updatedList));
      setList(updatedList);
      if (page.current === 1) {
        setTotalListCount(total ?? 0);
      }
      setIsLoading(false);
    }
  }, [isPayoutTxnsFetching]);

  useEffect(() => {
    if (!isPayoutTxnsFetching && isPayoutTxnsError) {
      setIsLoading(false);
      setHasMore(false);
      if (isPermissionDenied(payoutTxnsError?.['data']?.code, payoutTxnsError?.['data']?.message)) {
        setHasPermissionsToView(false);
      }
    }
  }, [isPayoutTxnsError]);

  const resetListAndLoadMore = () => {
    page.current = 0;
    setList([]);
    setGroupedList({});
    loadMore();
  };

  useEffect(() => {
    resetListAndLoadMore();
  }, [queryParams]);

  const loadMore = useCallback(() => {
    setIsLoading(true);
    if (currentTeam?.id) {
      const payload: TransactionsByPayoutIdPayload = {
        payoutId,
        teamId: currentTeam?.id,
        limit: 10,
        page: page.current,
        ...queryParams
      };
      try {
        getPayoutTransactionsData({
          ...payload
        }).unwrap();
      } catch (err) {
        setIsError(true);
      }
    }
    page.current = page.current + 1;
  }, [currentTeam?.id, page.current, queryParams]);

  const resetQuery = () => {
    resetListAndLoadMore();
  };

  return {
    isLoading: isLoading,
    isFetching: isPayoutTxnsFetching,
    totalListCount,
    list,
    groupedList,
    isError,
    loadMore,
    resetQuery,
    hasMore,
    setQueryParams,
    queryParams,
    isLoadingSuccess: isPayoutTxnsSuccess,
    hasPermissionsToView
  };
};
